import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
// import { CountrySelect } from '../common/CountrySelect';
import SupplierProfileBuilderCountryBadge from './SupplierProfileBuilderCountryBadge';
import type { SupplierCompanyProfile } from '../../types/types';

interface RestrictedFieldsSet {
  ubo_details: string;
  credit_score_value: string;
  credit_score_provider: string;
  is_financials_public: boolean;
  last_financial_audit_date?: string;
}

interface SupplierProfileBuilderRestrictedTabProps {
  restrictedFields: RestrictedFieldsSet;
  setRestrictedFields: React.Dispatch<
    React.SetStateAction<RestrictedFieldsSet>
  >;
  profile: SupplierCompanyProfile;
  setProfile: React.Dispatch<React.SetStateAction<SupplierCompanyProfile>>;
  tempOriginCountry: string;
  setTempOriginCountry: React.Dispatch<React.SetStateAction<string>>;
  setChangesSaved: React.Dispatch<React.SetStateAction<boolean>>;
  addOriginCountry: () => void;
  countries: { id: number; name: string; code: string }[];
  isSaving: boolean;
  onSave: () => void;
}

const SupplierProfileBuilderRestrictedTab: React.FC<
  SupplierProfileBuilderRestrictedTabProps
> = ({
  restrictedFields,
  setRestrictedFields,
  profile,
  setProfile,
  tempOriginCountry,
  setTempOriginCountry,
  setChangesSaved,
  addOriginCountry,
  isSaving,
  countries,
  onSave,
}) => {
  return (
    <Form className="p-3 pt-0">
      {restrictedFields.is_financials_public ? (
        <div className="alert alert-warning mb-4">
          <i className="bi bi-shield-lock-fill me-2"></i>
          This information is <strong>available</strong> to all users, including
          non-logged in buyers. Consider the implications of making this public.
        </div>
      ) : (
        <div className="alert alert-info mb-4">
          <i className="bi bi-shield-lock-fill me-2"></i>
          This information is <strong>restricted to logged-in buyers</strong> by
          default unless you enable public access below.
        </div>
      )}

      <Row className="mb-4">
        <Col>
          <Form.Check
            type="switch"
            id="is-public-switch"
            label="Make this entire tab public"
            checked={restrictedFields.is_financials_public || false}
            onChange={(e) => {
              setRestrictedFields({
                ...restrictedFields,
                is_financials_public: e.target.checked,
              });
              setChangesSaved(false);
            }}
          />
          <Form.Text className="text-muted">
            Turn this on if you want non-logged in users to see your financial
            and identity details.
          </Form.Text>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col md={6}>
          <h5 className="mb-3">Financial & Identity</h5>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Ultimate Beneficial Owner (UBO)</Form.Label>
                <Form.Control
                  placeholder="Full names of owners with >25%"
                  value={restrictedFields.ubo_details || ''}
                  onChange={(e) =>
                    setRestrictedFields({
                      ...restrictedFields,
                      ubo_details: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Credit Score</Form.Label>
                <Form.Control
                  placeholder="e.g. AAA or 750"
                  value={restrictedFields.credit_score_value || ''}
                  onChange={(e) =>
                    setRestrictedFields({
                      ...restrictedFields,
                      credit_score_value: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Score Provider</Form.Label>
                <Form.Control
                  placeholder="e.g. Creditinfo"
                  value={restrictedFields.credit_score_provider || ''}
                  onChange={(e) =>
                    setRestrictedFields({
                      ...restrictedFields,
                      credit_score_provider: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Last Financial Audit Date</Form.Label>
                <Form.Control
                  type="date"
                  value={restrictedFields.last_financial_audit_date || ''}
                  onChange={(e) =>
                    setRestrictedFields({
                      ...restrictedFields,
                      last_financial_audit_date: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <h5 className="mb-3">Supply Chain Transparency</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tier 1 Visibility (Direct Suppliers)</Form.Label>
                <Form.Select
                  value={profile.tier_1_visibility_level || 'unknown'}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      tier_1_visibility_level: e.target.value,
                    })
                  }
                >
                  <option value="unknown">Unknown</option>
                  <option value="identified">
                    Identified (We know who they are)
                  </option>
                  <option value="risk_assessed">
                    Risk Assessed (Screened for ESG)
                  </option>
                  <option value="verified">Verified (Audited/Certified)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tier 2 Visibility (Sub-suppliers)</Form.Label>
                <Form.Select
                  value={profile.tier_2_visibility_level || 'none'}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      tier_2_visibility_level: e.target.value,
                    })
                  }
                >
                  <option value="none">No Visibility</option>
                  <option value="critical_only">Critical Path Only</option>
                  <option value="partial">Partial Mapping (~50%)</option>
                  <option value="full">Full Transparency</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Raw Material Origins (Countries)</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <div className="flex-grow-1">
                    {/* <CountrySelect
                      full
                      value={tempOriginCountry}
                      onChange={(val) => setTempOriginCountry(val as string)}
                    /> */}
                    <select
                      className="form-select"
                      value={tempOriginCountry || ''}
                      onChange={(e) => setTempOriginCountry(e.target.value)}
                    >
                      <option value="">Select a country...</option>
                      {countries
                        .filter(
                          (c) =>
                            !profile.raw_material_origins?.includes(c.code),
                        )
                        .map((country) => (
                          <option key={country.id} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                    </select>
                    <label className="form-text">
                      Select a country and click "Add" to include it in your
                      profile.
                    </label>
                  </div>
                  <div className="mt-1">
                    <Button
                      variant="outline-primary"
                      onClick={addOriginCountry}
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {profile.raw_material_origins?.map((code: string) => {
                    const country = countries.find((c) => c.code === code);
                    return (
                      <SupplierProfileBuilderCountryBadge
                        key={code}
                        countryId={country ? country.id.toString() : '0'}
                        countryName={country ? country.name : code}
                        removeCountry={() => {
                          setProfile({
                            ...profile,
                            raw_material_origins:
                              profile.raw_material_origins?.filter(
                                (c: string) => c !== code,
                              ) || [],
                          });
                          setChangesSaved(false);
                        }}
                      />
                    );
                  })}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      <Button variant="primary" onClick={onSave} disabled={isSaving}>
        {isSaving ? 'Saving...' : 'Save Profile'}
      </Button>
    </Form>
  );
};

export default SupplierProfileBuilderRestrictedTab;
